import React, { useCallback, useEffect, useState } from 'react';

import { Select } from 'components/ui/select';
import { buildCreateBody, createObject, loadObject, search } from '../api';
import type { QboError, QuickbooksEntity } from '../types';
import DisplayNameForm from './display_name_form';
import QuickbooksSelectMenu from './menu';
import { serverErrors } from 'helpers/api_errors';

type CreateFields = Record<string, unknown>;

const isDuplicateNameError = (qboError: QboError): boolean => {
  if (!qboError.Fault?.Error || !Array.isArray(qboError.Fault?.Error)) return false;
  return qboError.Fault!.Error!.some(
    (err: { Message: string }) => err?.Message?.includes('Duplicate Name Exists Error'));
};

interface Props {
  buildoutObject?: Record<string, unknown>;
  // When rendered in a list, the parent resolves every row's value in one bulk query and passes it
  // in via `initialValue` with `hydrated` set, so each row skips its own fetch-on-mount. A missing
  // `initialValue` while `hydrated` means the linked entity no longer exists in QuickBooks.
  hydrated?: boolean;
  initialValue?: QuickbooksEntity | null;
  quickbooksId?: number;
  type: 'customer' | 'vendor';
  onChange: (value: QuickbooksEntity | null) => void;
}

export default function QuickbooksSelect({
  buildoutObject, hydrated, initialValue, quickbooksId, type, onChange, ...props
}: Props) {
  const [loadedValue, setLoadedValue] = useState<QuickbooksEntity | null>(
    hydrated ? initialValue ?? null : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [menuIsOpen, setMenuIsOpen] = useState<boolean | undefined>(undefined);
  const [createError, setCreateError] = useState('');
  const [otherError, setOtherError] = useState('');
  const [loadError, setLoadError] = useState(
    hydrated && quickbooksId && !initialValue ? `This QuickBooks ${type} no longer exists.` : '',
  );
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (hydrated) return; // Parent hydrated the value; no per-row fetch.
    if (!quickbooksId) return;
    setIsLoading(true);
    setLoadError('');
    loadObject(
      { type, id: quickbooksId },
      (result: QuickbooksEntity) => {
        setLoadedValue(result);
        setIsLoading(false);
      },
      (error: unknown) => {
        setIsLoading(false);
        const [message] = serverErrors(error);
        setLoadError(typeof message === 'string' ? message : `This QuickBooks ${type} no longer exists.`);
      },
    );
  }, [type, quickbooksId]);

  const handleChange = useCallback((value: QuickbooksEntity | null) => {
    setCreateError('');
    setOtherError('');
    setLoadError('');
    setSuccessMessage('');
    setLoadedValue(value);
    onChange(value);
  }, [onChange]);

  const onCreate = useCallback((result: QuickbooksEntity) => {
    setIsCreating(false);
    setCreateError('');
    setOtherError('');
    setSuccessMessage(`Created QuickBooks ${type} ${result.displayName}`);
    setLoadedValue(result);
    setMenuIsOpen(false);
    setLoadedValue(result);
    onChange(result);
  }, [onChange]);

  const onError = useCallback((fields: CreateFields, error: QboError) => {
    if (isDuplicateNameError(error)) {
      setIsCreating(false);
      setCreateError(String(fields.displayName || fields.name || `${fields.givenName} ${fields.familyName}`));
      setMenuIsOpen(false);
    } else {
      setIsCreating(false);
      setMenuIsOpen(false);
      setOtherError(error?.Fault?.Error?.[0]?.Message ?? '');
    }
  }, []);

  const searchEntities = useCallback((searchQuery: string, callback: (results: QuickbooksEntity[]) => void) => {
    search({ displayName: searchQuery, type }, callback);
  }, [type]);

  const handleCreateClick = useCallback((displayName?: string) => {
    const fields: CreateFields = { type, ...buildCreateBody(type, buildoutObject) };
    if (displayName) {
      fields.displayName = displayName;
    }

    setIsCreating(true);
    setOtherError('');
    setSuccessMessage('');
    createObject(
      fields,
      onCreate,
      onError,
    );
  }, [type, buildoutObject, onChange, onCreate, onError]);

  const qbSelectProps = {
    isCreating,
    type,
    onCreateClick: buildoutObject && !isCreating && !createError ? () => handleCreateClick() : undefined
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;

  return (
    <>
      <Select
        {...props}
        aria-label="search"
        components={{ Menu: QuickbooksSelectMenu }}
        getOptionLabel={(entity: QuickbooksEntity) => entity.displayName}
        getOptionValue={(entity: QuickbooksEntity) => entity.id}
        isClearable
        isLoading={isLoading}
        loadOptions={searchEntities}
        menuIsOpen={menuIsOpen}
        menuPlacement="top"
        placeholder={`Enter QuickBooks ${type} name`}
        value={loadedValue}
        onChange={handleChange}
        onMenuOpen={() => setMenuIsOpen(undefined)}
        {...qbSelectProps}
      />
      {createError && (
        <DisplayNameForm
          failedDisplayName={createError}
          isCreating={isCreating}
          type={type}
          onCancel={() => setCreateError('')}
          onSubmit={handleCreateClick}
        />
      )}
      {otherError && (
        <div className="text-danger small mt-1">{otherError}</div>
      )}
      {loadError && (
        <div className="text-danger small mt-1">{loadError}</div>
      )}
      {successMessage && (
        <div className="text-success small mt-1">{successMessage}</div>
      )}
    </>
  );
}
